/**
 * Core analytics pipeline: compose → enrich → dispatch (CleverTap + Google Analytics).
 */

import { getCookie, getPageType } from "@repo/shared-utils/common-utils";
import { store } from "@repo/global-store/react";
import { isMobile } from "@repo/shared-utils/mobile-utils";
import { logErrorToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";
import { getSessionData, type UserSessionData } from "@repo/shared-utils/session";
import { COMMON_SSO_ACCESS_TOKEN, PERPETUAL_TOKEN } from "@repo/shared-utils/sso.constants";
import { clevertapEvent } from "../utils/clevertapevent-utils";
import {
  ANALYTICS_DEBUG_KEY,
  ANALYTICS_E2E_PAGE_LOAD_TIME_SECONDS_KEY,
  FULLY_LOADED,
  GA_LOGIN_STATE_TEXT,
  PAGE_TAG_1,
  PAGE_TAG_2,
  PAGE_TAG_3,
} from "../constants/analytics.constants";
import { CleverTapConstants } from "../constants/clevertap.constants";
import { mapCleverTapToGA } from "./mappers";
import type {
  ActivityTimeLog,
  AnalyticsWindow,
  CleverTapPayload,
  ComponentEventInput,
  DataLayerProps,
  DataLayerState,
  DispatchContext,
  GaEventType,
  LoginStatus,
} from "../types";

let _activityTimeLog: ActivityTimeLog = {
  ctViewdEventTime: 0,
  ctClickEventTime: 0,
  viewEventTriggered: false,
};

// Throttle utility
export const throttle = <
  // biome-ignore lint/suspicious/noExplicitAny: cannot know type of args AOT
  Fn extends (...args: any[]) => any,
>(
  fn: Fn,
  delay: number
) => {
  let timerId: ReturnType<typeof setTimeout> | undefined;
  return (...args: Parameters<Fn>) => {
    if (timerId) return;
    timerId = setTimeout(() => {
      fn(...args);
      timerId = undefined;
    }, delay);
  };
};

// Scroll count with throttling and closure
export const getScrollCount = (() => {
  let scrollCount = 0;
  const scrollHandler = throttle(() => scrollCount++, 500);
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("scroll", scrollHandler);
  }
  return () => scrollCount;
})();

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

export const getModuleName = (moduleName: string): string => {
  return `${moduleName}${getPageType(PAGE_TAG_1) ?? ""}`;
};

export const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

// ---------------------------------------------------------------------------
// Compose data-layer props (camelCase → snake_case normalisation)
// ---------------------------------------------------------------------------

export const composeDataLayerProps = (
  input: ComponentEventInput,
  gaEventType: GaEventType
): DataLayerProps => {
  const buildClickType = (componentName: string, clickType?: string): string | undefined => {
    const trimmedClickType = clickType?.trim();
    if (!trimmedClickType) return undefined;

    const componentKey = componentName.trim().toLowerCase();
    const clickKey = trimmedClickType.toLowerCase();
    if (componentKey && clickKey.includes(componentKey)) {
      return trimmedClickType;
    }
    return componentKey ? `${componentKey}_${trimmedClickType}` : trimmedClickType;
  };

  return stripUndefined({
    component: input.componentName,
    cta_text: input.ctaText,
    entry_point: input.entryPoint,
    section_title: input.sectionTitle,
    sub_section: input.subSection,
    destination_url: input.destinationUrl,
    event: gaEventType,
    event_type: input.eventType,
    click_type: buildClickType(input.componentName, input.clickType),
    element: input.element,
    tab_position: input.tabPosition !== undefined ? String(input.tabPosition) : undefined,
    journey_tag: input.journeyTag,
    stage_tag: input.stageTag,
    validation_error: input.validationError,
    form_name: input.formName,
    field_name: input.fieldName,
    field_value: input.fieldValue,
    submission_status: input.submissionStatus,
    time_spent: input.timeSpent,
    application_id: input.applicationId,
    product_category: input.productCategory,
    product_name: input.productName,
    page_type: input.pageType,
    page_url: input.pageUrl,
    unique_asset_id: input.uniqueAssetId,
    input_amount: input.inputAmount,
    offer_amount: input.offerAmount,
    component_id: input.componentId,
    nudge_type: input.nudgeType,
    nudge_value: input.nudgeValue,
    component_position: input.componentPosition,
    segment_name: input.segmentName,
    sprint_release: input.sprintRelease,
    loan_variant: input.loanVariant,
    loan_range: input.loanRange,
    amount_ai: input.amountAi,
    tenure: input.tenure,
    tenure_ai: input.tenureAi,
    roi: input.roi,
    roi_ai: input.roiAi,
    monthly_instalment: input.monthlyInstalment,
    native_app_version: input.nativeAppVersion,
    custom_attr1: input.customAttr1,
    custom_attr2: input.customAttr2,
    custom_attr3: input.customAttr3,
    load_type: input.loadType,
  });
};

// ---------------------------------------------------------------------------
// Build enriched data-layer object (async — fetches session, page metadata)
// ---------------------------------------------------------------------------

export const buildDataLayerObject = async (baseProps: DataLayerProps): Promise<DataLayerState> => {
  const dataLayerObject: DataLayerState = { ...baseProps };

  if (typeof window !== "undefined") {
    dataLayerObject.page_url = window.location.href;
    const analyticsWindow = window as AnalyticsWindow;
    const nativeAppVersion = isOpenInApp()
      ? (analyticsWindow.tarsier?.invokePrompt?.(APP_PROMPTS.APP_VERSION) as string)
      : "";
    dataLayerObject.native_app_version = nativeAppVersion;
  }

  const pageType = getPageType(PAGE_TAG_1);
  const productCategory = getPageType(PAGE_TAG_2);
  const productName = getPageType(PAGE_TAG_3);
  if (pageType) dataLayerObject.page_type = pageType;
  if (productCategory) dataLayerObject.product_category = productCategory;
  if (productName) dataLayerObject.product_name = productName;

  let session: UserSessionData | undefined;
  try {
    session = await getSessionData();
    if (session?.custId) dataLayerObject.customer_id = session.custId;
    if (session?.custType) dataLayerObject.customer_tag = session.custType.toLowerCase();
  } catch {
    // proceed without session data
  }

  const ctLoginStatus = resolveLoginStatus();
  dataLayerObject.is_loggedin = resolveGaLoginState(ctLoginStatus);

  return dataLayerObject;
};

// ---------------------------------------------------------------------------
// Compose CleverTap payload
// ---------------------------------------------------------------------------

export const composeCleverTapPayload = (
  input: ComponentEventInput,
  dataLayerObject: DataLayerState
): CleverTapPayload => {
  const campaignCookie = safeParseCookie<Record<string, string>>("campaignCookie");
  const lastClickCookie = safeParseCookie<Record<string, string>>("LastClickCookie");
  const gaId = getCookie("_ga") || undefined;
  const pageName = computePageName(dataLayerObject);
  const epSource = resolveEpSource(
    lastClickCookie?.click,
    input.moduleName,
    dataLayerObject,
    pageName
  );
  const pageObject = getClevertapPageObject();
  const loginStatusForCt = resolveLoginStatus();
  let sanitizedOverrides = input.cleverTapOverrides
    ? sanitizeToStrings(input.cleverTapOverrides)
    : undefined;
  const bannerFlag = sanitizedOverrides?.isBannerComponent;
  const bannerFieldsEnabled = shouldAttachBannerFields(bannerFlag);
  if (sanitizedOverrides) {
    const { isBannerComponent: _bannerFlag, ...rest } = sanitizedOverrides;
    sanitizedOverrides = rest;
  }

  const hostname = typeof window !== "undefined" ? window.location.hostname : undefined;
  const language =
    typeof document !== "undefined" && typeof document.documentElement?.lang === "string"
      ? document.documentElement.lang
      : undefined;
  const analyticsWindow = typeof window !== "undefined" ? (window as AnalyticsWindow) : undefined;

  const basePayload = sanitizeToStrings({
    EVENT_NAME: input.eventName,
    EP_COMPONENT_NAME: dataLayerObject.component,
    EP_SECTION: dataLayerObject.section_title,
    EP_SUB_SECTION: dataLayerObject.sub_section,
    EP_CTA_TEXT: dataLayerObject.cta_text,
    EP_DESTINATION: dataLayerObject.destination_url,
    EP_POSITION: input.position ?? dataLayerObject.tab_position,
    EP_CLICK_TYPE: dataLayerObject.click_type,
    EP_EVENT_TYPE: dataLayerObject.event_type,
    EP_ID: dataLayerObject.unique_asset_id,
    EP_ENTRYPOINT: dataLayerObject.entry_point,
    EP_OFFER_AMOUNT: dataLayerObject.offer_amount,
    EP_REFERENCE_ID: dataLayerObject.component_id,
    EP_NUDGE: dataLayerObject.nudge_type,
    EP_NUDGE_VALUE: dataLayerObject.nudge_value,
    EP_SR_NUMBER: dataLayerObject.component_position,
    EP_SEGMENT: dataLayerObject.segment_name,
    EP_SPRINT: dataLayerObject.sprint_release,
    EP_SEARCH: getSearchTerm(),
    EP_LOAN_VARIANT: dataLayerObject.loan_variant,
    EP_LOAN_RANGE: dataLayerObject.loan_range,
    EP_AMOUNT_AI: dataLayerObject.amount_ai,
    EP_TENURE: dataLayerObject.tenure,
    EP_TENURE_AI: dataLayerObject.tenure_ai,
    EP_ROI: dataLayerObject.roi,
    EP_ROI_AI: dataLayerObject.roi_ai,
    EP_EMI: dataLayerObject.monthly_instalment,
    EP_AMOUNT_ENTERED: dataLayerObject.input_amount,
    EP_NATIVE_VERSION: dataLayerObject.native_app_version,
    EP_SUCCESS_EVENT_TYPE: dataLayerObject.submission_status,
    EP_PAGE_NAME: pageName,
    EP_PAGE_URL: dataLayerObject.page_url,
    EP_PROPERTY_TYPE: dataLayerObject.page_type,
    EP_HOSTNAME: hostname,
    EP_LANG: language,
    EP_SOURCE: epSource,
    EP_USER_TYPE: dataLayerObject.customer_tag
      ? dataLayerObject.customer_tag.toUpperCase()
      : undefined,
    EP_CUSTOMER_TYPE: dataLayerObject.customer_tag
      ? dataLayerObject.customer_tag.toUpperCase()
      : undefined,
    EP_JOURNEY: input.journey ?? dataLayerObject.journey_tag,
    EP_STAGE: dataLayerObject.stage_tag,
    EP_BUSINESS: dataLayerObject.product_name,
    EP_PRODUCT_NAME: dataLayerObject.product_name,
    EP_PRODUCT_CATEGORY: dataLayerObject.product_category,
    EP_PLATFORM: isOpenInApp() ? CleverTapConstants.APP : CleverTapConstants.WEB,
    DEVICE_TYPE: resolveDeviceType(),
    EP_LOGIN_STATUS: loginStatusForCt,
    EP_GA_ID: gaId,
    EP_LAST_CLICK: lastClickCookie?.click,
    EP_UTM_SOURCE: campaignCookie?.utm_source,
    EP_UTM_MEDIUM: campaignCookie?.utm_medium,
    EP_UTM_CAMPAIGN: campaignCookie?.utm_campaign,
    EP_UTM_CONTENT: campaignCookie?.utm_content,
    EP_CT_ID: getCleverTapId(),
    EP_TIMESTAMP: new Date().toLocaleString(),
    EP_LOAD_TYPE: dataLayerObject.load_type,
    EP_LOAD_TIME: getPageLoadTime(dataLayerObject),
    EP_PAGE_LOAD: getPageLoadType(dataLayerObject),
    EP_ACTIVITY_TIMELOG: getActivityTimeLog(),
    EP_SCROLL: String(getScrollCount()),
    EP_VALIDATION_ERROR: dataLayerObject.validation_error,
    EP_APPLICATION_ID: dataLayerObject.application_id,
    EP_CUST_ATTRIBUTE_1: dataLayerObject.custom_attr1,
    EP_CUST_ATTRIBUTE_2: dataLayerObject.custom_attr2,
    EP_CUST_ATTRIBUTE_3: dataLayerObject.custom_attr3,
    JOURNEY: input.journey ?? dataLayerObject.journey_tag,
    CUSTOMER_ID: input.customerId ?? dataLayerObject.customer_id,
    CAMPAIGN: input.campaign ?? dataLayerObject.campaign_tag,
    PWA_VERSION: input.pwaVersion ?? dataLayerObject.pwa_version,
    APPLICATION_ID: input.applicationId ?? dataLayerObject.application_id,
    UTM_CAMPAIGN: input.utmCampaign ?? dataLayerObject.utm_campaign,
    UTM_MEDIUM: input.utmMedium ?? dataLayerObject.utm_medium,
    UTM_CONTENT: input.utmContent ?? dataLayerObject.utm_content,
    EP_FOLD: `fold_${getScrollCount()}`,
  });

  return {
    ...basePayload,
    ...(bannerFieldsEnabled ? buildBannerFields(dataLayerObject) : {}),
    ...pageObject,
    ...(sanitizedOverrides ?? {}),
  };
};

// ---------------------------------------------------------------------------
// Derive GA event type
// ---------------------------------------------------------------------------

export const deriveGaEventType = (explicit?: GaEventType, eventType?: string): GaEventType => {
  if (explicit) return explicit;

  if (eventType) {
    const normalized = eventType.toLowerCase();
    const allowed: GaEventType[] = ["view", "click", "scroll", "impression", "load", "prefilled"];
    if ((allowed as string[]).includes(normalized)) return normalized as GaEventType;

    if (normalized.includes("view")) return "view";
    if (normalized.includes("load")) return "load";
    if (normalized.includes("scroll")) return "scroll";
    if (normalized.includes("impress")) return "impression";
    if (normalized.includes("prefill")) return "prefilled";
  }

  return "click";
};

// ---------------------------------------------------------------------------
// Dispatch: fires CleverTap first, then pushes GA
// ---------------------------------------------------------------------------

export const dispatchAnalyticsEvent = async (
  eventName: string,
  ctPayload: CleverTapPayload,
  dataLayerObject: DataLayerState,
  context: DispatchContext
): Promise<void> => {
  const analyticsWindow = (typeof window !== "undefined" ? window : undefined) as
    AnalyticsWindow | undefined;
  const debugBehavior = analyticsWindow?.[ANALYTICS_DEBUG_KEY];

  if (eventName) {
    updateActivityTimeLog();
    try {
      if (debugBehavior?.console) {
        console.log("[CT]", eventName, ctPayload);
      }
      if (debugBehavior?.fireEvents !== false) {
        clevertapEvent(eventName, ctPayload);
      }
    } catch (error) {
      const errorMessage = toErrorMessage(error);
      logErrorToNewRelic(`Failed to send CleverTap event: ${errorMessage}`, {
        eventName,
        componentName: context.componentName,
      });
      console.error(`Failed to send CT event ${eventName}: ${errorMessage}`);
    }
  }

  try {
    const gaPayload = mapCleverTapToGA({
      ctPayload,
      dataLayer: dataLayerObject,
      destinationUrl: context.destinationUrl,
      position: context.position,
      gaEventType: context.gaEventType,
      overrides: context.gaOverrides,
    });
    if (debugBehavior?.console) {
      console.log("[GA]", gaPayload.event, gaPayload);
    }
    if (debugBehavior?.fireEvents === false) return;

    if (analyticsWindow) {
      analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
      analyticsWindow.dataLayer.push(gaPayload);
    }
  } catch (error) {
    const errorMessage = toErrorMessage(error);
    logErrorToNewRelic(`Failed to send GA event: ${errorMessage}`, {
      gaEventType: context.gaEventType,
      componentName: context.componentName ?? "unknown",
    });
    console.error(`Failed to send GA event ${eventName}: ${errorMessage}`);
  }
};

// ---------------------------------------------------------------------------
// Activity time log helpers
// ---------------------------------------------------------------------------

export const markViewEventTriggered = (): void => {
  _activityTimeLog = {
    ..._activityTimeLog,
    ctViewdEventTime: Date.now(),
    viewEventTriggered: true,
  };
};

const updateActivityTimeLog = (): void => {
  if (_activityTimeLog.viewEventTriggered) {
    _activityTimeLog = {
      ..._activityTimeLog,
      ctClickEventTime: Date.now(),
      viewEventTriggered: false,
    };
  }
};

// ---------------------------------------------------------------------------
// EP_SOURCE for in-app
// ---------------------------------------------------------------------------

export function transformString(input: string) {
  const words = input?.trim()?.split(/\s+/);
  return words.length > 1
    ? words.map((word) => word[0]?.toUpperCase()).join("")
    : input.toUpperCase();
}

export function stampEPSource(src: string) {
  if (!src || src.trim() === "") return "";

  const source = transformString(src);
  const ep_source_read = {
    action: "read",
    type: "string",
    key: "EP_SOURCE_CLICKSTREAM",
    value: "",
  };
  let epSourceRead = prompt(`SharedPref://${JSON.stringify(ep_source_read)}`) ?? "";
  const regexPattern = new RegExp(`_${source}`, "g");
  epSourceRead = epSourceRead?.replace(regexPattern, "");
  const newEpSourceRead = epSourceRead ? `${epSourceRead}_${source}` : source;
  const ep_source_write_parent = {
    action: "write",
    type: "string",
    key: "EP_SOURCE_PARENT",
    value: newEpSourceRead,
  };
  prompt(`SharedPref://${JSON.stringify(ep_source_write_parent)}`);
  const ep_source_write = {
    action: "write",
    type: "string",
    key: "EP_SOURCE_CLICKSTREAM",
    value: newEpSourceRead,
  };
  prompt(`SharedPref://${JSON.stringify(ep_source_write)}`);
  return newEpSourceRead;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const stripUndefined = <T extends Record<string, unknown>>(record: T): T => {
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (value !== undefined && value !== null) {
      clean[key] = value;
    }
  }
  return clean as T;
};

const sanitizeToStrings = (record: Record<string, unknown>): CleverTapPayload => {
  const clean: CleverTapPayload = {};
  for (const [key, value] of Object.entries(record)) {
    if (value !== undefined && value !== null && value !== "") {
      if (typeof value === "string") {
        clean[key] = value;
      } else if (typeof value === "object") {
        clean[key] = JSON.stringify(value);
      } else {
        clean[key] = String(value);
      }
    }
  }
  return clean;
};

const resolveDeviceType = (): string => {
  if (isOpenInApp()) return CleverTapConstants.DEVICEAPP;
  return isMobile ? CleverTapConstants.MOBWEB : CleverTapConstants.DESKWEB;
};

/**
 * Reads login status from global store.
 */
const readGlobalLoginStatus = (): LoginStatus | undefined => {
  try {
    return store.getState()?.auth?.loginStatus;
  } catch {
    return undefined;
  }
};

/**
 * Checks for SSO tokens to infer login when global store hasn't resolved yet.
 */
const hasReliableSsoTokens = (): boolean => {
  if (typeof window === "undefined") return false;

  try {
    const accessToken = localStorage.getItem(COMMON_SSO_ACCESS_TOKEN);
    if (accessToken && accessToken !== "dummy") return true;
  } catch {
    // storage access may throw in sandboxed iframes
  }

  const pToken = getCookie(PERPETUAL_TOKEN);
  return pToken.length > 0;
};

const resolveLoginStatus = (): "logged-in" | "non-logged-in" => {
  const status = readGlobalLoginStatus();

  if (status === "success" || status === "perpetual") return "logged-in";
  if (status === "failure") return "non-logged-in";

  // Store still "pending" or unavailable — fall back to token presence
  return hasReliableSsoTokens() ? "logged-in" : "non-logged-in";
};

/** Maps CleverTap login label to GA login state text. */
const resolveGaLoginState = (ctLoginStatus: "logged-in" | "non-logged-in"): string => {
  const storeStatus = readGlobalLoginStatus() ?? "pending";
  if (storeStatus === "perpetual") return GA_LOGIN_STATE_TEXT.perpetual;
  return ctLoginStatus === "logged-in" ? GA_LOGIN_STATE_TEXT.success : GA_LOGIN_STATE_TEXT.failure;
};

const getCleverTapId = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const analyticsWindow = window as AnalyticsWindow;
  return analyticsWindow.clevertap?.getCleverTapID?.() ?? undefined;
};

const getClevertapPageObject = (): CleverTapPayload => {
  if (typeof window === "undefined") return {};
  const analyticsWindow = window as AnalyticsWindow;
  const pageObject = analyticsWindow.clevertap?.ctPageObject ?? {};
  return sanitizeToStrings(pageObject);
};

const safeParseCookie = <T>(name: string): T | undefined => {
  const raw = getCookie(name);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
};

const computePageName = (dataLayer: DataLayerState): string | undefined => {
  if (dataLayer.product_name) {
    const suffix = dataLayer.page_type ? ` ${dataLayer.page_type}` : "";
    return `${dataLayer.product_name}${suffix}`.replace(/-/g, " ").toUpperCase();
  }
  if (dataLayer.product_category) {
    return dataLayer.product_category.replace(/-/g, " ").toUpperCase();
  }
  return undefined;
};

const resolveEpSource = (
  lastClick?: string,
  moduleName?: string,
  dataLayerObject?: DataLayerState,
  pageName?: string
): string | undefined => {
  if (isOpenInApp()) {
    if (moduleName && moduleName.trim() !== "") return stampEPSource(moduleName);
    return "NA";
  }

  if (lastClick && lastClick.trim() !== "") return lastClick;

  if (typeof window !== "undefined") {
    const resolvedPath = (() => {
      if (window.location.pathname && window.location.pathname.trim() !== "") {
        return window.location.pathname;
      }
      try {
        return new URL(window.location.href).pathname || "NA";
      } catch {
        return "NA";
      }
    })();

    const contextTitle =
      dataLayerObject?.section_title ||
      dataLayerObject?.product_name ||
      pageName ||
      (dataLayerObject?.page_type ? dataLayerObject.page_type.toUpperCase() : "NA");

    const sourceContext = dataLayerObject?.page_type || dataLayerObject?.product_category || "WEB";

    return `${resolvedPath || "NA"} > ${sourceContext} > ${contextTitle || "NA"}`;
  }

  return "NA";
};

export const getSearchTerm = (): string => {
  const readFirstString = (cookieName: string): string | undefined => {
    const raw = getCookie(cookieName);
    if (!raw) return undefined;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return undefined;
      const first = parsed[0];
      if (typeof first !== "string") return undefined;
      const term = first.trim();
      return term !== "" ? term : undefined;
    } catch {
      return undefined;
    }
  };
  return readFirstString("recentSearchKeys") ?? readFirstString("recentSearchExperia") ?? "";
};

const getCachedE2EPageLoadSeconds = (): number | undefined => {
  if (typeof window === "undefined") return undefined;
  const analyticsWindow = window as AnalyticsWindow;
  const cachedSeconds = analyticsWindow[ANALYTICS_E2E_PAGE_LOAD_TIME_SECONDS_KEY];
  if (typeof cachedSeconds === "number" && Number.isFinite(cachedSeconds) && cachedSeconds >= 0)
    return cachedSeconds;
  return undefined;
};

const getPageLoadTime = (dataLayerObject: DataLayerProps): string | undefined => {
  const cachedSeconds = getCachedE2EPageLoadSeconds();
  if (cachedSeconds !== undefined) return `${cachedSeconds.toFixed(2)} SECONDS`;
  return dataLayerObject.page_load_time;
};

const getPageLoadType = (dataLayerObject: DataLayerProps): string | undefined => {
  if (getCachedE2EPageLoadSeconds() !== undefined) return FULLY_LOADED;
  return dataLayerObject.page_load_type;
};

const getActivityTimeLog = (): string | undefined => {
  const { ctClickEventTime, ctViewdEventTime } = _activityTimeLog;
  const activityTime = ctClickEventTime - ctViewdEventTime;
  if (activityTime > 0) return `${(activityTime / 1000).toFixed(2)} SECONDS`;
  return undefined;
};

const buildBannerFields = (dataLayerObject: DataLayerState): CleverTapPayload => {
  const toOptionalString = (value?: string | number | boolean): string | undefined => {
    if (value === undefined || value === null || value === "") return undefined;
    return String(value);
  };

  let moduleName: string | undefined;
  if (dataLayerObject.page_type && dataLayerObject.product_category) {
    moduleName = `${dataLayerObject.page_type}_${dataLayerObject.product_category}`;
  }

  let moduleSection: string | undefined;
  if (dataLayerObject.page_type === "homepage") {
    moduleSection = dataLayerObject.section_title;
  } else if (dataLayerObject.page_type && dataLayerObject.component) {
    moduleSection = `${dataLayerObject.page_type}_${dataLayerObject.component}`;
  }

  return sanitizeToStrings({
    EP_BANNER_CTA: toOptionalString(dataLayerObject.cta_text),
    EP_BANNER_PRODUCT: toOptionalString(dataLayerObject.product_name),
    EP_BANNER_SEQUENCE: toOptionalString(dataLayerObject.tab_position),
    EP_BANNER_TEXT: toOptionalString(dataLayerObject.section_title),
    EP_BANNER_MODULE_NAME: moduleName,
    EP_BANNER_SUBDIVISION: toOptionalString(dataLayerObject.section_title),
    EP_BANNER_TYPE: toOptionalString(dataLayerObject.component),
    EP_BANNER_CLICK_TYPE: toOptionalString(dataLayerObject.cta_text),
    EP_BANNER_DESCRIPTION: toOptionalString(dataLayerObject.section_title),
    EP_BANNER_MODULE_SECTION: moduleSection,
  });
};

const shouldAttachBannerFields = (flag?: string): boolean => {
  if (!flag) return false;
  return flag.trim().toLowerCase() === "true";
};
